import React from 'react';
import { CBCTypeId } from '../../config/constants';

/**
 * Common component for displaying price comparisons with color coding
 *
 * Logic:
 * - For CBC Costing Type (when costingTypeId === CBCTypeId):
 *   - Price Increase (new > old) = GREEN (good for business)
 *   - Price Decrease (new < old) = RED (bad for business)
 *
 * - For Non-CBC Costing Types (VBC, ZBC, etc.):
 *   - Price Increase (new > old) = RED (bad - costs more)
 *   - Price Decrease (new < old) = GREEN (good - costs less)
 *
 * - No Change (new = old) = NO COLOR
 *
 * @param {Object} props
 * @param {number|string} props.newValue - New/revised value
 * @param {number|string} props.oldValue - Old/existing value
 * @param {number} props.costingTypeId - Costing type ID to determine color logic (optional)
 * @param {string} props.children - Content to display
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.title - Tooltip text
 * @param {string} props.id - Element ID
 * @param {Object} props.style - Inline styles
 */
const PriceComparisonSpan = ({
    newValue,
    oldValue,
    costingTypeId,
    children,
    className = '',
    title = '',
    id = '',
    style = {},
    ...otherProps
}) => {
    // Convert to numbers for comparison
    const newPrice = Number(newValue || 0);
    const oldPrice = Number(oldValue || 0);

    // Check if this is CBC costing type
    const isCBC = costingTypeId === CBCTypeId;

    // Determine color class based on costing type
    let colorClass = '';

    if (isCBC) {
        // CBC Logic: Opposite of normal
        if (newPrice > oldPrice) {
            colorClass = 'green-value'; // Price increase = GREEN for CBC
        } else if (newPrice < oldPrice) {
            colorClass = 'red-value'; // Price decrease = RED for CBC
        }
    } else {
        // Normal Logic: VBC, ZBC, and others
        if (newPrice > oldPrice) {
            colorClass = 'red-value'; // Price increase = RED
        } else if (newPrice < oldPrice) {
            colorClass = 'green-value'; // Price decrease = GREEN
        }
    }
    // No color class if prices are equal

    // Combine classes
    const finalClassName = `${className} ${colorClass}`.trim();

    return (
        <span
            className={finalClassName}
            title={title}
            id={id}
            style={style}
            {...otherProps}
        >
            {children}
        </span>
    );
};

export default PriceComparisonSpan;

/**
 * Usage Examples:
 *
 * // Normal costing (VBC/ZBC) - increase shows red
 * <PriceComparisonSpan newValue={150} oldValue={100} className="form-control">
 *     $150.00
 * </PriceComparisonSpan>
 *
 * // CBC costing - increase shows green (opposite logic)
 * <PriceComparisonSpan
 *     newValue={150}
 *     oldValue={100}
 *     costingTypeId={CBCTypeId}
 *     className="form-control"
 * >
 *     $150.00
 * </PriceComparisonSpan>
 *
 * // No change - no color for any costing type
 * <PriceComparisonSpan newValue={100} oldValue={100} className="form-control">
 *     $100.00
 * </PriceComparisonSpan>
 *
 * // Price decrease - green for normal, red for CBC
 * <PriceComparisonSpan newValue={80} oldValue={100} costingTypeId={costingTypeId} className="form-control">
 *     $80.00
 * </PriceComparisonSpan>
 */